/**
 * แคว้นเก้ากระบี่ (九劍州, the Nine Swords March), docs/archive/OPEN_WORLD.md "Round 2": the 3,456 m Yanyu region mirrored
 * north-south so the sea lies SOUTH (as in the new Yanyu), with the new Yanyu (data/layouts/yanyu2.ts, the 600 m river
 * town, plan B) stamped at its coast in place of the old 160 m village. Everything else is the Yanyu region's: the hub
 * ลานศิลาเก้ากระบี่, the Wudang range, the stone forest, the pleasure valley, the roads and the qinggong routes, only
 * with y turned over (`region/flip.ts`). The old region and village are kept (`yanyu.ts`).
 *
 * Metres in the region: x east, y south. The core's tile (0, 0) is at (1188, 2500): its river mouth and sea edge
 * (core y 556) meet the region's south coast (y 3056) and the region's river runs into its north end.
 */
import { flipBlockout, flipSketch } from '../../region/flip';
import type { BlockoutBox, RegionSketch, XY } from '../../region/sketch';
import { YANYU_BLOCKOUT } from './yanyu.blockout';
import { YANYU_REGION } from './yanyu';
import { JIUZHOU_CAMPS } from './jiuzhou.content';

/** The stamped 600 m core: where its tile (0, 0) lies in the region, and its side. */
export const JIUZHOU_CORE = { x: 1188, y: 2500, size: 600 } as const;

const inCore = (x: number, y: number, pad = 0): boolean =>
  x >= JIUZHOU_CORE.x - pad && x < JIUZHOU_CORE.x + JIUZHOU_CORE.size + pad && y >= JIUZHOU_CORE.y - pad && y < JIUZHOU_CORE.y + JIUZHOU_CORE.size + pad;

/** Where the segment from `outside` to `inside` crosses the core's edge. */
function edgeCross(outside: XY, inside: XY): XY {
  const { x, y, size } = JIUZHOU_CORE;
  let best = 1;
  const [ox, oy] = outside;
  const [dx, dy] = [inside[0] - ox, inside[1] - oy];
  for (const [p, d, lo, hi] of [
    [ox, dx, x, x + size],
    [oy, dy, y, y + size],
  ] as const) {
    if (d === 0) continue;
    for (const edge of [lo, hi]) {
      const t = (edge - p) / d;
      const qx = ox + dx * t;
      const qy = oy + dy * t;
      if (t > 0 && t < best && inCore(qx, qy, 1)) best = t;
    }
  }
  return [ox + dx * best, oy + dy * best];
}

/** A road's points with those inside the core cut off at its edge (the core has its own roads). */
function clipToCore(pts: readonly XY[]): XY[] {
  const out: XY[] = [];
  for (let k = 0; k < pts.length; k++) {
    const p = pts[k]!;
    if (!inCore(p[0], p[1])) {
      out.push(p);
      continue;
    }
    const prev = pts[k - 1];
    const next = pts[k + 1];
    if (prev && !inCore(prev[0], prev[1])) out.push(edgeCross(prev, p));
    else if (next && !inCore(next[0], next[1])) out.push(edgeCross(next, p));
  }
  return out;
}

/** How far the waystation moves up the trunk road (m). */
const WAY_DY = -156;

const mirrored = flipSketch(YANYU_REGION, {
  id: 'jiuzhou',
  core: { x: JIUZHOU_CORE.x, y: JIUZHOU_CORE.y, layout: 'yanyu2' },
  drop: (x, y) => inCore(x, y),
});

/**
 * The stone forest's scatter box lies over the core's north edge after the mirror (its spires inside the core would be
 * stamped over): circles of avoided ground cover the part of the core (and a 40 m pad) that the box reaches, so the
 * spires keep to the open country.
 */
function coreAvoid(box: readonly [number, number, number, number]): [number, number, number][] {
  const out: [number, number, number][] = [];
  const x0 = Math.max(box[0], JIUZHOU_CORE.x - 40);
  const x1 = Math.min(box[0] + box[2], JIUZHOU_CORE.x + JIUZHOU_CORE.size + 40);
  const y0 = Math.max(box[1], JIUZHOU_CORE.y - 40);
  const y1 = Math.min(box[1] + box[3], JIUZHOU_CORE.y + JIUZHOU_CORE.size + 40);
  for (let y = y0; y <= y1 + 35; y += 70) for (let x = x0; x <= x1 + 35; x += 70) out.push([x, y, 50]);
  return out;
}

export const JIUZHOU_REGION: RegionSketch = {
  ...mirrored,
  spires: { ...mirrored.spires, avoid: [...(mirrored.spires.avoid ?? []), ...coreAvoid(mirrored.spires.box)] },
  // The mirrored hunting grounds, and the camps of the empty grass and forests (jiuzhou.content.ts).
  wilds: [...(mirrored.wilds ?? []), ...JIUZHOU_CAMPS],
  // The village's roads end at the core's edge; the trunk road to the city meets the core's north road at (1488, 2500).
  roads: mirrored.roads
    .map((r) => ({ ...r, pts: clipToCore(r.pts) }))
    .filter((r) => r.pts.length >= 2),
  // The river from the lake valley runs into the core's river (core x 396, y 0), not the old bay.
  rivers: mirrored.rivers.map((r) => {
    if (r.name !== 'แม่น้ำสราญ') return r;
    const keep = 14;
    return {
      ...r,
      pts: [...r.pts.slice(0, keep), [1850, 2620], [1720, 2550], [JIUZHOU_CORE.x + 396, JIUZHOU_CORE.y - 4]] as XY[],
      bed: [...r.bed.slice(0, keep), 6, 3.5, 0.4],
      w: [...r.w.slice(0, keep), 38, 42, 46],
    };
  }),
  // The coast plain: ground levelled round the core so the 600 m town (flat at sea level) meets the region without a wall
  // at the seam; it rises to the hub's plain (22 m) over the 700 m between them, under the 0.35 m per tile step limit.
  flats: [...mirrored.flats, { x: JIUZHOU_CORE.x + 300, y: JIUZHOU_CORE.y + 300, r: 520, h: 0.3, name: 'ที่ราบหยานหยู', noise: 0.6 }],
  // The west pass's circle stands in a tree: one step north is open ground.
  // The grid keeps plains south and mist-peak west of the new Yanyu, so their warps keep their sides.
  portals: mirrored.portals.map((p) => {
    const q = p.to === 'bell-stone' ? { ...p, y: p.y - 1 } : p;
    return p.to === 'plains' || p.to === 'mist-peak' ? { ...q, side: YANYU_REGION.portals.find((o) => o.to === p.to)!.side } : q;
  }),
  // The old village's own station is the core's.
  // Stop ids are global (data/carriage.ts `ROAD_STOPS`): the old region's `waystation` and `hub` become `jz-*` here.
  stations: [
    { id: 'yanyu2', name: 'หยานหยู', x: JIUZHOU_CORE.x + 300, y: JIUZHOU_CORE.y + 340 },
    ...mirrored.stations.filter((s) => !inCore(s.x, s.y)).map((s) => (s.id === 'hub' ? { ...s, id: 'jz-hub' } : s)),
    { id: 'jz-waystation', name: 'ศาลาพักม้าสิบลี้', x: 1510, y: YANYU_REGION.size - 935 + WAY_DY },
  ],
  grounds: [...mirrored.grounds, { x: 1510, y: YANYU_REGION.size - 932 + WAY_DY, rw: 24, rh: 22, cover: 'town' as const }],
};

/**
 * The ten-li waystation: halfway on the trunk road once, it would now fall inside the 600 m core, so it moves 156 m up
 * the road, out of it (its boxes, station and ground).
 */
const isWay = (b: BlockoutBox): boolean => b.id.startsWith('waystation.');

/**
 * The turtle's bay (jz-site-turtle, arena 1885,3311): a wrecked ship that the sea left aground, as greybox boxes.
 * The main deck is walked on (a pier-kind deck 0.75 m over the sea); the stern castle is a solid mass with a standable top;
 * five stepping stones (0.7 m over the sea, like the bay's) lead from the east beach to it, so a skimmer reaches it from
 * the beach rock (`wb-turtle:rock-1`). The world-boss mechanics read the deck as the site `wb-turtle:deck`
 * (data/worldBossMech.ts); change one with the other.
 */
export const JIUZHOU_WRECK_BOXES: readonly BlockoutBox[] = [
  { id: 'wreck.deck', place: 0, name: 'ซากเรือล่มปากอ่าว (ดาดฟ้า)', lane: 'wreck', kind: 'pier', x: 1862, y: 3291, w: 14, d: 6, h: 0.75, base: 0, note: 'walkable deck of the turtle fight; a skimmer steps up onto it' },
  { id: 'wreck.stern', place: 0, name: 'ซากเรือล่มปากอ่าว (ท้ายเรือ)', lane: 'wreck', kind: 'stone', x: 1856, y: 3291, w: 3, d: 6, h: 2.5, base: 0, note: 'raised stern: a perch above the deck' },
  ...[
    [1872, 3291],
    [1877, 3292],
    [1882, 3293],
    [1887, 3293.5],
    [1891, 3294],
  ].map(
    ([x, y], k): BlockoutBox => ({ id: `wreck.stone.${k + 1}`, place: 0, name: 'หินเหยียบข้ามอ่าวไปซากเรือ', lane: 'wreck', kind: 'stone', x: x!, y: y!, w: 3, d: 3, h: 0.7, base: 0 }),
  ),
];

/** The mirrored greybox: the city, the range, the stone forest and the qinggong routes; the old bay's boxes are left out. */
export const JIUZHOU_BLOCKOUT: readonly BlockoutBox[] = [
  ...flipBlockout(YANYU_BLOCKOUT.filter((b) => !isWay(b)), YANYU_REGION.size, (x, y) => inCore(x, y, 40)),
  ...flipBlockout(YANYU_BLOCKOUT.filter(isWay), YANYU_REGION.size).map((b) => ({ ...b, y: b.y + WAY_DY })),
  ...JIUZHOU_WRECK_BOXES,
];
